import Phaser from 'phaser';
import { loadScores } from '../storage/high-scores';
import { getJournal, getUnlockedPresets } from '../storage/journal';
import { audio } from '../audio';
import { EMERGENT_TRAITS } from '../sim/traits/emergent';
import type { Genome } from '../sim/genome';

export class TitleScene extends Phaser.Scene {
  private selectedPreset: Genome[] | undefined = undefined;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    const { width, height } = this.scale.gameSize;
    const cx = width / 2;

    // Scale down on narrow portrait screens so the heading doesn't overflow.
    const titleSize = Math.min(64, Math.max(32, Math.floor(width / 7)));
    this.add
      .text(cx, height / 2 - 130, 'Ball Breeder', {
        fontFamily: 'system-ui',
        fontSize: `${titleSize}px`,
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, height / 2 - 70, 'click to start', {
        fontFamily: 'system-ui',
        fontSize: '20px',
        color: '#9aa0a6',
      })
      .setOrigin(0.5);

    // Help link at the bottom: opens the codex in a new tab so the title
    // scene (and any in-flight audio context) stays put.
    const help = this.add
      .text(cx, height - 28, 'codex — what does each trait do?', {
        fontFamily: 'system-ui',
        fontSize: '13px',
        color: '#9aa0a6',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    help.on('pointerover', () => help.setColor('#ff9ec4'));
    help.on('pointerout', () => help.setColor('#9aa0a6'));
    help.on(
      'pointerup',
      (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        window.open('./help.html', '_blank');
      },
    );

    // High score list.
    const scores = loadScores();
    if (scores.length > 0) {
      this.add
        .text(cx, height / 2 - 20, 'best runs', {
          fontFamily: 'system-ui',
          fontSize: '14px',
          color: '#ff9ec4',
        })
        .setOrigin(0.5);
      const lines = scores.slice(0, 5).map((s, i) => {
        const mm = String(Math.floor(s.time / 60)).padStart(2, '0');
        const ss = String(s.time % 60).padStart(2, '0');
        return `${String(i + 1).padStart(2)}.  ${mm}:${ss}    kills ${String(s.kills).padStart(3)}    breeds ${String(s.breeds).padStart(2)}`;
      });
      this.add
        .text(cx, height / 2 + 12, lines.join('\n'), {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#c8c8c8',
          align: 'center',
          lineSpacing: 4,
        })
        .setOrigin(0.5, 0);
    }

    // Version number — bottom-left, small gray text.
    this.add
      .text(8, height - 8, `v${__APP_VERSION__}`, {
        fontFamily: 'system-ui',
        fontSize: '11px',
        color: '#555555',
      })
      .setOrigin(0, 1);

    // Journal button — bottom-right.
    const journalBtn = this.add
      .text(width - 12, height - 28, 'journal', {
        fontFamily: 'system-ui',
        fontSize: '13px',
        color: '#9aa0a6',
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    journalBtn.on('pointerover', () => journalBtn.setColor('#ff9ec4'));
    journalBtn.on('pointerout', () => journalBtn.setColor('#9aa0a6'));
    journalBtn.on(
      'pointerup',
      (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.showJournalOverlay();
      },
    );

    // Preset selector — shown only when at least one preset is unlocked.
    const presets = getUnlockedPresets();
    if (presets.length > 0) {
      this.buildPresetSelector(cx, height / 2 - 42, presets);
    }

    this.input.once('pointerdown', async () => {
      // First user gesture — unlock audio before anything plays.
      await audio.unlock();
      this.scene.start('RunScene', { presetGenomes: this.selectedPreset });
    });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Row of preset buttons: [Random] [Speed Flock] … */
  private buildPresetSelector(
    cx: number,
    y: number,
    presets: { name: string; genomes: Genome[] }[],
  ): void {
    const all = [{ name: 'Random', genomes: [] as Genome[] }, ...presets];
    const btnW = 110;
    const gap = 8;
    const totalW = all.length * btnW + (all.length - 1) * gap;
    const startX = cx - totalW / 2;

    const buttons: Phaser.GameObjects.Text[] = [];

    const refresh = (): void => {
      buttons.forEach((b, i) => {
        const isSelected =
          all[i]!.genomes === this.selectedPreset ||
          (all[i]!.genomes.length === 0 && this.selectedPreset === undefined);
        b.setColor(isSelected ? '#ff9ec4' : '#9aa0a6');
      });
    };

    for (let i = 0; i < all.length; i++) {
      const preset = all[i]!;
      const bx = startX + i * (btnW + gap) + btnW / 2;
      const btn = this.add
        .text(bx, y, preset.name, {
          fontFamily: 'system-ui',
          fontSize: '13px',
          color: i === 0 ? '#ff9ec4' : '#9aa0a6',
          backgroundColor: '#1a1d23',
          padding: { x: 8, y: 4 },
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setStyle({ backgroundColor: '#2a2d33' }));
      btn.on('pointerout', () => btn.setStyle({ backgroundColor: '#1a1d23' }));
      btn.on(
        'pointerup',
        (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          this.selectedPreset = preset.genomes.length > 0 ? preset.genomes : undefined;
          refresh();
        },
      );
      buttons.push(btn);
    }
  }

  /** Full-screen overlay listing all emergent traits (green = discovered, gray = ???). */
  private showJournalOverlay(): void {
    const { width, height } = this.scale.gameSize;
    const discovered = getJournal();

    const overlay = this.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.88)
      .setDepth(500)
      .setScrollFactor(0)
      .setInteractive(); // absorb clicks so they don't fall through

    const title = this.add
      .text(width / 2, 48, 'Genetics Journal', {
        fontFamily: 'system-ui',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#ff9ec4',
      })
      .setOrigin(0.5)
      .setDepth(501)
      .setScrollFactor(0);

    const traitTexts: Phaser.GameObjects.Text[] = [];
    EMERGENT_TRAITS.forEach((t, i) => {
      const found = discovered.includes(t.id);
      const label = found ? `✓  ${t.label} — ${t.description}` : `???`;
      const txt = this.add
        .text(width / 2, 90 + i * 30, label, {
          fontFamily: 'system-ui',
          fontSize: '14px',
          color: found ? '#88ff88' : '#555566',
        })
        .setOrigin(0.5, 0)
        .setDepth(501)
        .setScrollFactor(0);
      traitTexts.push(txt);
    });

    const hint = this.add
      .text(width / 2, height - 44, `${discovered.length} / ${EMERGENT_TRAITS.length} discovered`, {
        fontFamily: 'system-ui',
        fontSize: '13px',
        color: '#9aa0a6',
      })
      .setOrigin(0.5)
      .setDepth(501)
      .setScrollFactor(0);

    const closeBtn = this.add
      .text(width / 2, height - 20, '[ close ]', {
        fontFamily: 'system-ui',
        fontSize: '14px',
        color: '#ff9ec4',
      })
      .setOrigin(0.5, 1)
      .setDepth(501)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    const allObjects: Phaser.GameObjects.GameObject[] = [
      overlay,
      title,
      hint,
      closeBtn,
      ...traitTexts,
    ];

    closeBtn.on(
      'pointerup',
      (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        allObjects.forEach((o) => o.destroy());
      },
    );
  }
}
